// Third Party
import { NextPage, InferGetServerSidePropsType } from 'next';
import moment from 'moment';

// Utils
import { routes, canonicalUrlForPath } from '@routes';
import {
  SearchProvider,
  getSearchServerSideProps,
} from '@contexts/search-context';

// UI
import { PublicLayout } from '@layouts/public-layout';
import { AccountShow } from '@pages-components/account-show';
import { MobileSearchModalProvider } from '@components/mobile-search-modal';

/**
 * getServerSideProps is implemented in @contexts/search-context
 *
 * This function is used to get the initial props for the page and reused throughout the app.
 */
export const getServerSideProps = getSearchServerSideProps;

const AccountPage: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
  account,
  cursorAfter,
  cursorBefore,
  features,
  initActiveFeatures,
  initAdultCount,
  initCheckInDate,
  initCheckOutDate,
  initChildCount,
  initInfantCount,
  initMapCoordinates,
  initMapZoom,
  initMinNumberOfBathrooms,
  initMinNumberOfRooms,
  initMinPrice,
  initMaxPrice,
  initPetCount,
  initPropertyTypes,
  initSearchType,
  placeId,
  session,
}) => {
  if (!account) throw new Error('Account not found');

  return (
    <>
      <PublicLayout
        session={session}
        loadingSession={false}
        canonicalUrl={canonicalUrlForPath(routes.accounts(account.slug))}
        pageTitle={`${account.companyName} Property Manager | Savvy`}
        pageDescription={`${account.companyName} Property Manager`}
        imageUrl={account.heroUrl}
      >
        <MobileSearchModalProvider>
          <SearchProvider
            accountId={account.id}
            allFeatures={features}
            initActiveFeatures={initActiveFeatures}
            initAdultCount={initAdultCount}
            initCheckInDate={initCheckInDate ? moment(initCheckInDate) : null}
            initCheckOutDate={
              initCheckOutDate ? moment(initCheckOutDate) : null
            }
            initChildCount={initChildCount}
            initInfantCount={initInfantCount}
            initMapCoordinates={
              initMapCoordinates ? initMapCoordinates : undefined
            }
            initMapZoom={initMapZoom ? initMapZoom : undefined}
            initMinNumberOfBathrooms={initMinNumberOfBathrooms}
            initMinNumberOfRooms={initMinNumberOfRooms}
            initMinPrice={initMinPrice}
            initMaxPrice={initMaxPrice}
            initPetCount={initPetCount}
            initPropertyTypes={initPropertyTypes}
            initSearchType={initSearchType}
            cursorAfter={cursorAfter}
            cursorBefore={cursorBefore}
            placeId={placeId}
            pageSize={6}
          >
            <AccountShow account={account} />
          </SearchProvider>
        </MobileSearchModalProvider>
      </PublicLayout>
    </>
  );
};

export default AccountPage;
